import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LngLatLike, Map, Marker } from 'maplibre-gl';
import { GetServerSideProps } from 'next';
import { toast } from 'sonner';
import { useRouter } from 'next/router';
import { TightLayout } from '@/components/layout/Layout';
import { MapContextProvider, useMap } from '@/hooks/useMap';
import * as MapFeaturesLayer from '@/components/ui/map/layers/MapFeatures';
import * as MapFeaturesData from '@/components/ui/map/sources/MapFeaturesData';
import { IMapOptions } from '@/types/map';
import { Props as MapProps } from '@/hooks/useMap';
import * as Toast from '@/components/ui/toast/Toast';
import * as DemoLocationsData from '@/components/ui/map/sources/DemoLocationsData';
import * as DemoLocationsLayer from '@/components/ui/map/layers/DemoLocations';
import { getDemoLocations, getImagesForDemoLocation } from '@/api/location';
import { AlmanacProvider, useAlmanac } from '@/hooks/useAlmanac';
import { AlmanacLocation } from '@/types/location';
import { getNumTimesMapped } from '@/api/segments';
import { Button } from '@/components/shadcn/button';
import { filterInvalidHeadings, snapToLine, stitch } from '@/utils/imagery';
import { sortSequencesByTimestamp } from '@/utils/sort';
import PlayerWithModal from '@/components/ui/almanac/FramePlayer/PlayerWithModal';
import CloseIcon from '@/components/ui/icons/CloseIcon';
import {
  addAlmanacSource,
  addHeightRestrictionHoverHandler,
  addHeightRestrictionLayer,
  addRoadWorkLayers,
  showPopupOnHeightRestrictionClick,
  showPopupOnRoadWorkClick,
} from '@/utils/map/almanac';

interface Props {
  country: string;
}

const INITIAL_LOCATIONS = [
  { lat: 34.04801, lon: -118.252586, zoom: 16, name: 'Los Angeles' }, // LA
  { lat: 37.78513, lon: -122.402975, zoom: 16, name: 'San Francisco' }, // SF
  { lat: 33.44566, lon: -112.072961, zoom: 16, name: 'Phoenix' }, // PHX
  { lat: 41.884178, lon: -87.627867, zoom: 15, name: 'Chicago' }, // CHI
];

function MapPage({ country }: Props) {
  const [screenWidth, setScreenWidth] = useState<number | undefined>();
  const options: IMapOptions = { maxZoom: 18, enableSearchMarker: true };
  const router = useRouter();
  const initialLocation = useMemo(
    () =>
      INITIAL_LOCATIONS[Math.floor(Math.random() * INITIAL_LOCATIONS.length)]!,
    [],
  );

  // Only init map at rand loc if no coordates params are set.
  const shouldInitWithRandomLocation = !router.query.center;

  const mapContextProps: MapProps = {
    country,
    screenWidth: screenWidth, // this defines what to show on the map by default
    options,
    ...(shouldInitWithRandomLocation ? initialLocation : undefined),
    mapType: 'light',
  };

  useEffect(() => {
    if (window) {
      setScreenWidth(window.innerWidth);
    }
  }, []);

  return (
    <TightLayout>
      {screenWidth && (
        <MapContextProvider {...mapContextProps}>
          <AlmanacProvider>
            <MapContent
              shouldInitWithRandomLocation={shouldInitWithRandomLocation}
              initialLocation={initialLocation}
            />
          </AlmanacProvider>
        </MapContextProvider>
      )}
    </TightLayout>
  );
}

function MapContent({
  shouldInitWithRandomLocation,
  initialLocation,
}: {
  shouldInitWithRandomLocation: boolean;
  initialLocation: LngLatLike & { name: string };
}) {
  const mapMounted = useRef(false);
  const { map, setAreLayersReady, mapContainerRef } = useMap();
  const [toastId, setToastId] = useState<string | number>();
  const {
    locations,
    setLocations,
    setActiveLocation,
    activeLocation,
    sortedSequences,
    setSortedSequences,
    activeFrameIndex,
    activeSequenceIndex,
    reset,
  } = useAlmanac();
  const [, setLocationNameMarkers] = useState<Marker[]>([]);
  const [beeMarker, setBeeMarker] = useState<Marker>();

  const fetchImagery = useCallback(
    async (activeLocation: AlmanacLocation) => {
      const controller = new AbortController();
      const signal = controller.signal;

      const data = await getImagesForDemoLocation(activeLocation._id, signal);

      if ('error' in data) {
        if (!signal.aborted) {
          toast.custom(t => (
            <Toast.Default
              title="Request Failed"
              description={data.error.message || data.error}
              buttons={[
                {
                  label: 'Dismiss',
                  onClick: () => toast.dismiss(t),
                  variant: 'secondary',
                  size: 'fw',
                },
              ]}
            />
          ));
        }

        setSortedSequences([[]]);
        return;
      }

      const { frames } = data;

      if (frames.length > 0) {
        const stitched = stitch(frames);
        let sorted = sortSequencesByTimestamp(stitched);

        if (activeLocation.segments) {
          const filtered = filterInvalidHeadings(
            sorted,
            activeLocation.segments,
          );
          sorted = snapToLine(filtered, activeLocation.segments);
        }

        setSortedSequences(sorted);
      }
    },
    [setSortedSequences],
  );
  useEffect(() => {
    if (!map) return;

    if (activeLocation) {
      DemoLocationsLayer.addLayers(map);
      fetchImagery(activeLocation);
    } else {
      DemoLocationsLayer.removeLayers(map);
      reset();
    }
  }, [activeLocation, fetchImagery, map, reset]);

  useEffect(() => {
    if (!map) return;

    setLocationNameMarkers(prev => {
      if (activeLocation && prev.length > 0) {
        prev.forEach(marker => {
          marker.remove();
        });
        return [];
      } else if (prev.length === 0 && locations?.length > 0) {
        return DemoLocationsLayer.addLocationMarkers({
          map,
          locations,
          onLocationClick: location =>
            setActiveLocation(location as AlmanacLocation),
        });
      }
      return [];
    });
  }, [activeLocation, locations, map, setActiveLocation]);

  const activeFrame = sortedSequences[activeSequenceIndex]?.[activeFrameIndex];
  // Update states when active location gets set or unset.
  useEffect(() => {
    if (!map) {
      return;
    }
    if (activeLocation && !beeMarker && activeFrame) {
      setBeeMarker(DemoLocationsLayer.addBeeIconMarker(map, activeFrame));
    } else if (!activeLocation && beeMarker) {
      beeMarker.remove();
      setBeeMarker(undefined);
    }
  }, [activeFrame, activeLocation, beeMarker, map]);

  const initializeData = useCallback(
    async (
      map: Map,
      setAreLayersReady: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      setAreLayersReady(false);

      // Add source and layer for points
      await MapFeaturesData.addSource(map);
      MapFeaturesLayer.addLayer(map);

      const { locations, error } = await getDemoLocations();

      if (!locations || locations.length === 0 || error) {
        console.error('No locations found');
        return;
      }

      setLocations(locations);
      await DemoLocationsData.addSource(map, locations);

      setAreLayersReady(true);

      // Only show intro toast if we are starting the map at a random location.
      if (shouldInitWithRandomLocation) {
        const toastId = toast.custom(
          t => (
            <Toast.Default
              title={`${initialLocation.name} Data Sample`}
              description="Speed limits, turn restrictions, traffic lights, stop signs and exit signs detected by Bee Maps AI."
              buttons={[
                {
                  label: 'Dismiss',
                  onClick: () => toast.dismiss(t),
                  variant: 'secondary',
                  size: 'fw',
                },
                {
                  label: 'Learn More',
                  onClick: () => {
                    window.open(
                      '/product/map-features-api',
                      '_blank',
                      'noopener,noreferrer',
                    );
                    toast.dismiss(t);
                  },
                  variant: 'default',
                  size: 'fw',
                },
              ]}
            />
          ),
          { duration: Infinity, className: 'right-10 -bottom-4' },
        );
        setToastId(toastId);
      }
    },
    [initialLocation.name, setLocations, shouldInitWithRandomLocation],
  );

  useEffect(() => {
    if (map && !mapMounted.current) {
      initializeData(map, setAreLayersReady);
      mapMounted.current = true;

      const addAlmanac = async () => {
        // Almanac source and layers
        addAlmanacSource(map);
        // Height restriction
        await addHeightRestrictionLayer(map);
        addHeightRestrictionHoverHandler(map);
        showPopupOnHeightRestrictionClick(map);
        // Road work
        await addRoadWorkLayers(map);
        showPopupOnRoadWorkClick(map);
      };
      addAlmanac();
    }
  }, [map, initializeData, setAreLayersReady]);

  useEffect(() => {
    return () => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [toastId]);

  // Update the bee marker's location and rotation on frame change.
  useEffect(() => {
    if (!map || !beeMarker) return;

    const activeFrame =
      sortedSequences[activeSequenceIndex]?.[activeFrameIndex];
    if (!activeFrame) return;
    DemoLocationsLayer.updateBeeIconMarker(map, beeMarker, activeFrame);
  }, [map, beeMarker, sortedSequences, activeSequenceIndex, activeFrameIndex]);

  return (
    <div ref={mapContainerRef} className="absolute inset-0 flex size-full">
      {activeLocation && <LocationInfo location={activeLocation} />}
      {activeLocation && (
        <div className="absolute left-1/2 top-6 z-50 flex w-[600px] max-w-[100vw] -translate-x-1/2 overflow-hidden rounded-[20px] border-[6px] border-neutral-1000">
          <PlayerWithModal />
        </div>
      )}
    </div>
  );
}

function LocationInfo({ location }: { location: AlmanacLocation }) {
  const { setActiveLocation } = useAlmanac();
  const [numTimesMapped, setNumTimesMapped] = useState<number>();
  useEffect(() => {
    // Fetch number of times mapped
    getNumTimesMapped(location.geojson)
      .then(res => {
        setNumTimesMapped(res.numTimesMapped);
      })
      .catch(err => {
        console.log(err);
      });
  }, [location.geojson]);
  return (
    <div className="absolute bottom-4 left-1/2 flex h-[50px] min-w-[600px] -translate-x-1/2 items-center justify-between rounded-[20px] bg-neutral-1000 px-2.5 py-4 shadow">
      <div className="hidden items-center justify-start gap-10 text-base font-bold sm:flex">
        <div className="flex items-center">
          <img
            src="/icons/bee-icon-colorful.png"
            alt="bee icon"
            className="h-[27px] w-[31px] rotate-90"
          />
          <div className="ml-2 truncate leading-none">{location.name}</div>
        </div>
        {numTimesMapped && (
          <div className="hidden truncate leading-none lg:flex">
            {numTimesMapped} drives
          </div>
        )}
      </div>

      <div className="flex items-center justify-start gap-2.5">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => {
            setActiveLocation(undefined);
          }}>
          <CloseIcon fill="white" />
        </Button>
      </div>
    </div>
  );
}

export default MapPage;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const country = req.headers['x-country'] || 'unknown';
  return {
    props: {
      country,
    },
  };
};
